import {
  ArtifactDocumentschemaFromJSON,
  FileTextSliceFromJSON,
  FileschemaFromJSON,
  instanceOfFileTextSlice,
  instanceOfFileschema,
  type ArtifactDocumentschema,
  type FileTextLineschema,
  type FileTextSlice,
  type Fileschema
} from '@reap/openapi-public-v1';
import { ApiResponseError } from '@app/utils/api';

export const FILE_AVAILABILITY_TEXT_READY = 'text_ready';
export const FILE_AVAILABILITY_PREVIEW_READY = 'preview_ready';
export const FILE_AVAILABILITY_DOWNLOAD_ONLY = 'download_only';
export const FILE_AVAILABILITY_NONE = 'none';
export type FileAvailabilityState =
  | typeof FILE_AVAILABILITY_TEXT_READY
  | typeof FILE_AVAILABILITY_PREVIEW_READY
  | typeof FILE_AVAILABILITY_DOWNLOAD_ONLY
  | typeof FILE_AVAILABILITY_NONE;

export const FILE_ANALYSIS_STATE_READY = 'ready';
export const FILE_ANALYSIS_STATE_NONE = 'none';
export const FILE_ANALYSIS_STATE_STALE = 'stale';
export const FILE_ANALYSIS_STATE_PARTIAL = 'partial';
export const FILE_ANALYSIS_STATE_FAILED = 'failed';
export type FileAnalysisState =
  | typeof FILE_ANALYSIS_STATE_READY
  | typeof FILE_ANALYSIS_STATE_NONE
  | typeof FILE_ANALYSIS_STATE_STALE
  | typeof FILE_ANALYSIS_STATE_PARTIAL
  | typeof FILE_ANALYSIS_STATE_FAILED;

export interface UploadedFilePreviewMetadata {
  availabilityState?: FileAvailabilityState | null;
  analysisState?: FileAnalysisState | null;
  analysisFamily?: string | null;
  preferredRep?: string | null;
  reps?: string[] | null;
  blockedReason?: string | null;
}

export type UploadedFileRecord = Fileschema & UploadedFilePreviewMetadata;

export interface ChatArtifactLike {
  id?: string;
  runId?: string;
  kind?: string | null;
  mimeType?: string | null;
  labels?: Record<string, string | undefined> | null;
}

export interface ReadableUploadLike {
  type?: string;
  name: string;
}

export type FileTextLineRecord = FileTextLineschema;
export type FileTextSliceRecord = FileTextSlice;

export const FILE_TEXT_SLICE_PAGE_SIZE = 80;

export const inferReadableUploadMime = (file: ReadableUploadLike): string => {
  const direct = file.type?.trim().toLowerCase() ?? '';
  if (direct) {
    return direct;
  }
  const name = file.name.trim().toLowerCase();
  if (name.endsWith('.txt') || name.endsWith('.log')) {
    return 'text/plain';
  }
  if (name.endsWith('.md') || name.endsWith('.markdown')) {
    return 'text/markdown';
  }
  if (name.endsWith('.csv')) {
    return 'text/csv';
  }
  if (name.endsWith('.tsv')) {
    return 'text/tab-separated-values';
  }
  if (name.endsWith('.json')) {
    return 'application/json';
  }
  if (name.endsWith('.yaml') || name.endsWith('.yml')) {
    return 'application/yaml';
  }
  if (name.endsWith('.pdf')) {
    return 'application/pdf';
  }
  if (name.endsWith('.pcap')) {
    return 'application/vnd.tcpdump.pcap';
  }
  if (name.endsWith('.pcapng')) {
    return 'application/x-pcapng';
  }
  if (name.endsWith('.xls')) {
    return 'application/vnd.ms-excel';
  }
  if (name.endsWith('.xlsx')) {
    return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  }
  if (name.endsWith('.zip')) {
    return 'application/zip';
  }
  if (name.endsWith('.jpg') || name.endsWith('.jpeg')) {
    return 'image/jpeg';
  }
  if (name.endsWith('.png')) {
    return 'image/png';
  }
  return 'application/octet-stream';
};

export const formatUploadedFilePreview = (file: UploadedFileRecord): string => {
  const parts = [
    file.filename ? `Name: ${file.filename}` : null,
    file.mimeType ? `Type: ${file.mimeType}` : null,
    typeof file.sizeBytes === 'number' ? `Size: ${Math.max(1, Math.round(file.sizeBytes / 1024))} KB` : null,
    file.description ? `Description: ${file.description}` : null
  ].filter(Boolean);
  return parts.length > 0 ? parts.join('\n') : 'Uploaded file';
};

export const parseArtifactDocument = (raw: string): ArtifactDocumentschema | null => {
  if (!raw) {
    return null;
  }
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return null;
    }
    try {
      return ArtifactDocumentschemaFromJSON(parsed);
    } catch {
      if ('content' in parsed || 'views' in parsed) {
        return parsed as ArtifactDocumentschema;
      }
      return null;
    }
  } catch {
    return null;
  }
};

export const parseUploadedFileRecord = (value: unknown): UploadedFileRecord | null => {
  if (!value || typeof value !== 'object' || !instanceOfFileschema(value as object)) {
    return null;
  }
  try {
    const base = FileschemaFromJSON(value);
    const record = value as Record<string, unknown>;
    return {
      ...base,
      availabilityState: typeof record.availabilityState === 'string' ? (record.availabilityState as FileAvailabilityState) : undefined,
      analysisState: parseFileAnalysisState(record.analysisState),
      analysisFamily: typeof record.analysisFamily === 'string' ? record.analysisFamily : undefined,
      preferredRep: typeof record.preferredRep === 'string' ? record.preferredRep : undefined,
      reps: Array.isArray(record.reps) ? record.reps.filter((rep): rep is string => typeof rep === 'string') : undefined,
      blockedReason: typeof record.blockedReason === 'string' ? record.blockedReason : undefined
    };
  } catch {
    return null;
  }
};

const parseFileAnalysisState = (value: unknown): FileAnalysisState | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }
  switch (value.trim().toLowerCase()) {
    case FILE_ANALYSIS_STATE_READY:
    case FILE_ANALYSIS_STATE_NONE:
    case FILE_ANALYSIS_STATE_STALE:
    case FILE_ANALYSIS_STATE_PARTIAL:
    case FILE_ANALYSIS_STATE_FAILED:
      return value.trim().toLowerCase() as FileAnalysisState;
    default:
      return undefined;
  }
};

export const buildFileTextContentPath = (fileId: string, startLine = 1, maxLines = FILE_TEXT_SLICE_PAGE_SIZE): string => {
  const params = new URLSearchParams();
  params.set('startLine', String(startLine));
  params.set('maxLines', String(maxLines));
  return `files/${encodeURIComponent(fileId)}/content?${params.toString()}`;
};

export const buildFileMetadataPath = (fileId: string): string => `files/${encodeURIComponent(fileId)}`;

export const buildFileDownloadPath = (fileId: string): string => `files/${encodeURIComponent(fileId)}/download`;

export interface ExistingFilePreviewState {
  fileRecord?: UploadedFileRecord;
  fileText?: FileTextSliceRecord;
}

export interface LoadFileArtifactPreviewArgs {
  fileId: string;
  full?: boolean;
  pageSize?: number;
  existing?: ExistingFilePreviewState;
  loadMetadata: (fileId: string) => Promise<UploadedFileRecord | null>;
  loadTextSlice: (fileId: string, startLine: number, maxLines: number) => Promise<FileTextSliceRecord | null>;
  loadDownloadBlob: (fileId: string, accept: string) => Promise<Blob>;
}

export interface LoadedFileArtifactPreview {
  availabilityState: FileAvailabilityState;
  fileRecord: UploadedFileRecord;
  content: string;
  fileText?: FileTextSliceRecord;
  objectUrl?: string;
  isFull: boolean;
}

export const resolveFileAvailabilityState = (file: {
  availabilityState?: string | null;
  mimeType?: string | null;
}): FileAvailabilityState => {
  const availabilityState = typeof file.availabilityState === 'string' ? file.availabilityState.trim().toLowerCase() : '';
  switch (availabilityState) {
    case FILE_AVAILABILITY_TEXT_READY:
    case FILE_AVAILABILITY_PREVIEW_READY:
    case FILE_AVAILABILITY_DOWNLOAD_ONLY:
    case FILE_AVAILABILITY_NONE:
      return availabilityState;
    default:
      break;
  }

  const mime = typeof file.mimeType === 'string' ? file.mimeType.trim().toLowerCase() : '';
  if (
    mime.startsWith('text/') ||
    mime === 'application/json' ||
    mime === 'application/yaml' ||
    mime === 'text/csv' ||
    mime === 'text/tab-separated-values'
  ) {
    return FILE_AVAILABILITY_TEXT_READY;
  }
  if (mime === 'application/pdf' || mime.startsWith('image/')) {
    return FILE_AVAILABILITY_PREVIEW_READY;
  }
  if (mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || mime === 'application/vnd.ms-excel') {
    return FILE_AVAILABILITY_DOWNLOAD_ONLY;
  }
  return FILE_AVAILABILITY_NONE;
};

export const formatFilePreviewErrorMessage = (error: unknown): string => {
  if (error instanceof ApiResponseError && (error.response.status === 404 || error.response.status === 409)) {
    return 'Readable preview unavailable for this file.';
  }
  if (error instanceof Error && error.message) {
    return error.message;
  }
  return 'Failed to load file.';
};

export const loadFileArtifactPreview = async ({
  fileId,
  full = false,
  pageSize = FILE_TEXT_SLICE_PAGE_SIZE,
  existing,
  loadMetadata,
  loadTextSlice,
  loadDownloadBlob
}: LoadFileArtifactPreviewArgs): Promise<LoadedFileArtifactPreview> => {
  const fileRecord = existing?.fileRecord ?? (await loadMetadata(fileId));
  if (!fileRecord) {
    throw new Error('Failed to parse file metadata.');
  }

  const availabilityState = resolveFileAvailabilityState(fileRecord);
  if (availabilityState === FILE_AVAILABILITY_TEXT_READY) {
    const startLine = full ? (existing?.fileText?.endLine ?? 0) + 1 : 1;
    const slice = await loadTextSlice(fileId, startLine, pageSize);
    if (!slice) {
      throw new Error('Failed to parse file content.');
    }
    const merged = full ? mergeFileTextSlices(existing?.fileText, slice) : slice;
    return {
      availabilityState,
      fileRecord,
      content: formatFileTextSlice(merged),
      fileText: merged,
      isFull: !hasMoreFileText(merged)
    };
  }

  if (availabilityState === FILE_AVAILABILITY_PREVIEW_READY) {
    const blob = await loadDownloadBlob(fileId, fileRecord.mimeType || '*/*');
    const objectUrl = URL.createObjectURL(blob);
    return {
      availabilityState,
      fileRecord,
      content: objectUrl,
      objectUrl,
      isFull: true
    };
  }

  return {
    availabilityState,
    fileRecord,
    content: '',
    isFull: true
  };
};

export const deriveFileDownloadName = (
  fileId: string,
  fileRecord?: Pick<UploadedFileRecord, 'filename'> | null,
  summary?: string | null
): string => {
  const explicit = fileRecord?.filename?.trim();
  if (explicit) {
    return explicit;
  }
  const fallback = summary?.trim();
  if (fallback) {
    return fallback;
  }
  return `file-${fileId}`;
};

export const downloadBlobAsFile = (blob: Blob, filename: string): void => {
  const url = URL.createObjectURL(blob);
  try {
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  } finally {
    URL.revokeObjectURL(url);
  }
};

export const parseFileTextSlice = (value: unknown): FileTextSliceRecord | null => {
  if (!value || typeof value !== 'object' || !instanceOfFileTextSlice(value as object)) {
    return null;
  }
  const candidate = value as {
    fileId?: unknown;
    mimeType?: unknown;
    startLine?: unknown;
    endLine?: unknown;
    totalLines?: unknown;
    lines?: unknown;
  };
  if (
    typeof candidate.fileId !== 'string' ||
    typeof candidate.mimeType !== 'string' ||
    !Number.isInteger(candidate.startLine) ||
    !Number.isInteger(candidate.endLine) ||
    !Number.isInteger(candidate.totalLines) ||
    !Array.isArray(candidate.lines) ||
    !candidate.lines.every(
      (line) =>
        line != null &&
        typeof line === 'object' &&
        Number.isInteger((line as { lineNo?: unknown }).lineNo) &&
        typeof (line as { text?: unknown }).text === 'string'
    )
  ) {
    return null;
  }
  try {
    return FileTextSliceFromJSON(value);
  } catch {
    return null;
  }
};

export const mergeFileTextSlices = (
  current: FileTextSliceRecord | undefined,
  next: FileTextSliceRecord
): FileTextSliceRecord => {
  if (!current) {
    return next;
  }
  if (current.fileId !== next.fileId) {
    return next;
  }

  const mergedByLine = new Map<number, string>();
  for (const line of current.lines) {
    mergedByLine.set(line.lineNo, line.text);
  }
  for (const line of next.lines) {
    mergedByLine.set(line.lineNo, line.text);
  }

  const mergedLines = Array.from(mergedByLine.entries())
    .sort((a, b) => a[0] - b[0])
    .map(([lineNo, text]) => ({ lineNo, text }));

  return {
    fileId: current.fileId,
    mimeType: next.mimeType || current.mimeType,
    startLine: Math.min(current.startLine, next.startLine),
    endLine: Math.max(current.endLine, next.endLine),
    totalLines: Math.max(current.totalLines, next.totalLines),
    lines: mergedLines
  };
};

export const formatFileTextSlice = (slice: FileTextSliceRecord): string => slice.lines.map((line) => line.text).join('\n');

export const hasMoreFileText = (slice: FileTextSliceRecord | undefined): boolean =>
  Boolean(slice && slice.totalLines > 0 && slice.endLine < slice.totalLines);

export const resolveArtifactPathWithContent = (
  resolveArtifactPath: (artifact: ChatArtifactLike) => string | null,
  artifact: ChatArtifactLike,
  full: boolean
): string | null => {
  const base = resolveArtifactPath(artifact);
  if (!base) {
    return null;
  }
  if (!full) {
    return base;
  }
  return base.includes('?') ? `${base}&content=full` : `${base}?content=full`;
};
